/**
 * useUserWithdrawData Hook
 * 탈퇴 요청 관리 데이터 훅
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import {useUserInfo} from "@/shared/hooks/useCrudOperations";
import {ApproveWithdrawalRequestDto, userWithdrawApi} from '../api/userWithdrawApi';
import {WithdrawRequest} from "@/features/system/userWithdraw/types/userWithdraw.types";
import {withdrawRequestListFromDto} from "@/features/system/userWithdraw/utils/userWithdrawHelpers";
import {ToastType} from "@/shared/components/feedback/Toast/Toast.types";
import {useToast} from "@/shared/components/feedback/Toast/ToastContext";
import {commonCodeApi} from '@/shared/api/commonCodeApi';
import {CommonCodeDto} from '@/shared/types/commonCode.types';

export const useUserWithdrawData = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [loading, setLoading] = useState(false);
    const [withdrawRequests, setWithdrawRequests] = useState<WithdrawRequest[]>([]);
    const [contStatusCodes, setContStatusCodes] = useState<CommonCodeDto[]>([]);
    const [withdrawalStatusCodes, setWithdrawalStatusCodes] = useState<CommonCodeDto[]>([]);

    const hasInitialized = useRef(false);

    // ======== 목록 조회 ========

    const fetchWithdrawRequests = useCallback(async (params?: {
        riderName?: string;
        suppName?: string;
        contStatus?: string;
        withdrawalStatusCd?: string;
    }) => {
        try {
            setLoading(true);
            const response = await userWithdrawApi.getWithdrawRequestList(params);
            setWithdrawRequests(response ? withdrawRequestListFromDto(response) : []);
        } catch {
            showToast('탈퇴 요청 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setWithdrawRequests([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // ======== 공통코드 조회 (계약상태 + 탈퇴요청상태 병렬) ========

    useEffect(() => {
        let isCancelled = false;

        const fetchCommonCodes = async () => {
            try {
                const [contStatusResponse, withdrawalStatusResponse] = await Promise.all([
                    commonCodeApi.getCommonCodeList('CONT_STATUS'),
                    commonCodeApi.getCommonCodeList('MEMBER_WITHDRAWAL_STATUS_CD'),
                ]);
                if (!isCancelled) {
                    setContStatusCodes(contStatusResponse.codes || []);
                    setWithdrawalStatusCodes(withdrawalStatusResponse.codes || []);
                }
            } catch {
                // 에러 무시 (드롭다운이 비어있어도 기능에 영향 없음)
            }
        };

        fetchCommonCodes();
        return () => { isCancelled = true; };
    }, []);

    // ======== 초기 데이터 로드 ========

    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            void fetchWithdrawRequests();
        }
    }, [fetchWithdrawRequests]);

    // ======== 탈퇴 승인 ========

    const approveWithdrawal = useCallback(async (requests: ApproveWithdrawalRequestDto[]) => {
        try {
            setLoading(true);
            const currentUserId = getUserInfo()?.userId || '';
            const requestsWithUserId = requests.map(req => ({ ...req, insertUserId: currentUserId }));
            const response = await userWithdrawApi.approveWithdrawal(requestsWithUserId);

            if (response && response.length > 0) {
                const allSuccess = response.every(item => item.success);
                if (allSuccess) {
                    showToast('탈퇴 요청이 승인되었습니다.', { type: ToastType.SUCCESS });
                } else {
                    const failedMessages = response
                        .filter(item => !item.success)
                        .map(item => item.message)
                        .join(', ');
                    showToast(`일부 탈퇴 요청 승인에 실패했습니다: ${failedMessages}`, { type: ToastType.ERROR });
                }
                await fetchWithdrawRequests();
                return response;
            }

            showToast('탈퇴 요청 승인에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } catch {
            showToast('탈퇴 요청 승인에 실패했습니다.', { type: ToastType.ERROR });
            return null;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchWithdrawRequests, getUserInfo]);

    return {
        loading,
        withdrawRequests,
        contStatusCodes,
        withdrawalStatusCodes,
        fetchWithdrawRequests,
        approveWithdrawal,
        refreshWithdrawRequests: fetchWithdrawRequests,
    };
};
